import {
  ArrowRightLeft, BarChart3, Building2, Compass, Gavel, Home, Layers,
  LineChart, MapPin, Shield, Sparkles, Target, Users, Zap,
} from 'lucide-react';

export const DATA_SOURCES = [
  'HM Land Registry',
  'Police crime data',
  'EPC certificates',
  'DfE schools',
  'Ofcom broadband',
  'ONS rental & postcode',
  'Planning designations',
  'Transport (NaPTAN)',
];

export const FRAGMENTED_SOURCES = [
  'Auction catalogues',
  'Estate agents',
  'Deal sourcers',
  'Land Registry',
  'Planning registers',
  'Rental evidence',
  'Area demographics',
  'Market comparables',
];

export const PLATFORM_STEPS = [
  {
    step: '01',
    title: 'Bring together',
    body: 'Auction lots, agent uploads, and deal-source listings in one searchable feed — not ten browser tabs.',
    icon: Layers,
  },
  {
    step: '02',
    title: 'Enrich',
    body: 'Layer neighbourhood, market, planning, rental, and risk context onto every opportunity automatically.',
    icon: Compass,
  },
  {
    step: '03',
    title: 'Score',
    body: 'See yield, price-vs-market, area trend, and an investment score so strong deals surface first.',
    icon: Target,
  },
  {
    step: '04',
    title: 'You decide',
    body: 'AI verdicts, comparables, and area reports help you act with confidence — or pass in minutes.',
    icon: Sparkles,
  },
];

export const INTELLIGENCE_PILLARS = [
  {
    title: 'Location',
    items: ['Crime & area risk', 'Schools & transport', 'Broadband & amenities'],
    icon: MapPin,
  },
  {
    title: 'Market',
    items: ['10-year sales history', 'Local price trends', 'Rental & yield context'],
    icon: LineChart,
  },
  {
    title: 'Planning',
    items: ['Conservation areas', 'Flood zones', 'Listed buildings & green belt'],
    icon: Building2,
  },
  {
    title: 'Property',
    items: ['Listing detail & photos', 'EPC & property type', 'Auction metadata'],
    icon: Home,
  },
  {
    title: 'Financial',
    items: ['Price vs estimated value', 'Gross yield', 'Comparable sales'],
    icon: BarChart3,
  },
  {
    title: 'Risk & demographics',
    items: ['IMD deprivation rank', 'Area growth signals', 'Lender-aware penalties'],
    icon: Shield,
  },
];

export const PRODUCT_PILLARS = [
  {
    eyebrow: 'Source opportunities',
    title: 'One feed for auctions, agents, and deal sources',
    body: 'PropScope aggregates stock from major UK auction houses and structured uploads — Future Property Auctions, GoTo Properties, Auction House, Agents Property Auction, and more — alongside estate-agent and deal-sourcer listings.',
    cta: 'Browse listings',
    to: '/register',
    icon: Gavel,
    accent: 'from-amber-500/20 to-orange-600/5',
  },
  {
    eyebrow: 'Evaluate faster',
    title: 'Investment intelligence on every property',
    body: '0–100 investment scores, gross yield, price bands vs estimated market value, AI Strong Buy / Buy / Hold / Avoid verdicts, and interactive neighbourhood maps with crime heatmaps.',
    cta: 'See all features',
    to: '/features',
    icon: BarChart3,
    accent: 'from-emerald-500/20 to-teal-600/5',
  },
  {
    eyebrow: 'Stay organised',
    title: 'From shortlist to portfolio',
    body: 'Watchlists, saved search profiles, email alerts, My Properties valuations, encrypted document vault, and broker sharing — keep the whole journey in one workspace.',
    cta: 'Start free trial',
    to: '/register',
    icon: Users,
    accent: 'from-sky-500/20 to-blue-600/5',
  },
];

export const SCORE_COMPONENTS = [
  { label: 'Price vs market', value: 35, max: 40 },
  { label: 'Gross yield', value: 22, max: 30 },
  { label: 'Area trend (10yr)', value: 14, max: 20 },
  { label: 'HMO opportunity', value: 8, max: 10 },
];

export const PERSONAS = [
  {
    title: 'Property investors',
    body: 'Filter deal flow by postcode, yield, and score. Spend less time assembling spreadsheets and more time on properties worth a viewing.',
    icon: Target,
  },
  {
    title: 'Auction houses & agents',
    body: 'Publish stock to investors who think in numbers. Structured uploads, auction metadata, and visibility inside a scoring engine.',
    icon: Gavel,
  },
  {
    title: 'Deal sourcers',
    body: 'Present opportunities with context — not just a PDF link. Let investors see yield, area data, and verdict before they call you back.',
    icon: Zap,
  },
];

export const COST_PILLARS = [
  {
    title: 'Time lost',
    body: 'Hours finding, assembling, and comparing information across portals and spreadsheets.',
    icon: ArrowRightLeft,
  },
  {
    title: 'Slower decisions',
    body: 'More time needed to assess an opportunity with confidence before someone else moves.',
    icon: BarChart3,
  },
  {
    title: 'Missed opportunities',
    body: 'The strongest deals discovered too late — or overlooked entirely in the noise.',
    icon: Target,
  },
];
